import { c as _c } from "react/compiler-runtime";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { mapProjectsByArchivingStateSelector, mapProjectsFetchingStatusSelector } from "@store/projects-map/projects-map-selectors";
import { fetchMapProjects } from "@store/projects-map/projects-map-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { sortProjects } from "@utils/project-utils";
import { matchesAnySearchWord } from "@utils/string-utils";
import { useEffect, useMemo } from "react";
/** All the projects arrive at once, so there is never a next page to load */
function loadNoMoreProjects() {
  // Nothing to load
}

/**
 * Lists the projects that pass filters the API cannot apply. They are taken from the projects
 * stream, which the map view shares, so the stream is fetched only once however often the user
 * switches between the views or changes the filters. Searching and sorting happen client-side too.
 */
export function useFilteredStreamedProjects(t0) {
  const $ = _c(18);
  const {
    companyId,
    projectArchivingState,
    sortBy,
    search,
    filterProjects,
    isEnabled
  } = t0;
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const fetchingStatus = useAppSelector(mapProjectsFetchingStatusSelector);
  let t1;
  if ($[0] !== projectArchivingState) {
    t1 = mapProjectsByArchivingStateSelector(projectArchivingState);
    $[0] = projectArchivingState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const projectsByArchivingStateSelector = t1;
  const allProjects = useAppSelector(projectsByArchivingStateSelector);
  let t2;
  let t3;
  if ($[2] !== companyId || $[3] !== dispatch || $[4] !== fetchingStatus || $[5] !== handleErrorWithToast || $[6] !== isEnabled) {
    t2 = () => {
      if (!isEnabled || fetchingStatus !== "uninitialized") {
        return;
      }
      dispatch(fetchMapProjects({
        companyId
      })).unwrap().catch(error => {
        handleErrorWithToast({
          id: `fetchStreamedProjects-${Date.now().toString()}`,
          title: "Failed to load the projects",
          error
        });
      });
    };
    t3 = [companyId, dispatch, fetchingStatus, handleErrorWithToast, isEnabled];
    $[2] = companyId;
    $[3] = dispatch;
    $[4] = fetchingStatus;
    $[5] = handleErrorWithToast;
    $[6] = isEnabled;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== allProjects || $[10] !== filterProjects || $[11] !== search || $[12] !== sortBy) {
    const searchedProjects = search ? allProjects.filter(project => matchesAnySearchWord(project.name, search)) : allProjects;
    t4 = sortProjects(filterProjects(searchedProjects), sortBy);
    $[9] = allProjects;
    $[10] = filterProjects;
    $[11] = search;
    $[12] = sortBy;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const projects = t4;
  const t5 = fetchingStatus === "uninitialized" || fetchingStatus === "pending";
  const t6 = fetchingStatus === "succeeded" || fetchingStatus === "rejected";
  let t7;
  if ($[14] !== projects || $[15] !== t5 || $[16] !== t6) {
    t7 = {
      projects,
      isFetchingProjects: t5,
      hasFetchedFirstPage: t6,
      nextPageCursor: undefined,
      loadMoreProjects: loadNoMoreProjects,
      totalCount: projects.length
    };
    $[14] = projects;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}